/**
 * LaunchAnnounceModal — Penlytics 公開 告知 (初回 1 回だけ 中央表示)
 *
 * 2026-06-19 ローンチ: 「本日公開 + ポイントが回る (消費⇄獲得⇄換金)」 を訴求。
 *   Penlytics 側 = ツール公開 + ポイント一周 を主役にしたコピー (= あいぺん側とは別内容)。
 *
 * 表示条件: localStorage に seen フラグが無い初回のみ (handoff/gift ページは除外)。
 * 動作: 「閉じる」 or 「ガイドを見る」 で seen フラグ保存 → 二度と自動表示しない。
 * 新しい告知は ANNOUNCE_ID を変えるだけで全 user に再表示。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const PRIMARY = '#4f46e5';
const PURPLE_GRAD = 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)';

const ANNOUNCE_ID = '2026-06-19';
const STORAGE_KEY = `penlytics_launch_announce_seen_${ANNOUNCE_ID}`;
const SHOW_DELAY_MS = 700;

const isSeen = () => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
};
const markSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
};

const Bullet = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
    <Box sx={{ mt: '7px', width: 6, height: 6, borderRadius: '50%', bgcolor: PRIMARY, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.84rem', color: '#0f1419', lineHeight: 1.7 }}>{children}</Typography>
  </Box>
);

const LaunchAnnounceModal = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // aipen→Penlytics handoff / 応援送信 フロー中は 邪魔しない
  const isBlockedPage =
    location.pathname.startsWith('/handoff') ||
    location.pathname.startsWith('/gift/');

  useEffect(() => {
    if (isBlockedPage || isSeen()) return;
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [isBlockedPage]);

  const close = useCallback(() => { setOpen(false); markSeen(); }, []);
  const goGuide = useCallback(() => { close(); navigate('/guide'); }, [close, navigate]);

  if (!open || isBlockedPage) return null;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      {/* ヘッダー (= Penlytics brand purple gradient) */}
      <Box sx={{ position: 'relative', background: PURPLE_GRAD, color: '#fff', px: 3, pt: 3.5, pb: 2.25 }}>
        <IconButton
          onClick={close}
          aria-label="閉じる"
          size="small"
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: 'rgba(255,255,255,0.85)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px',
          bgcolor: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
          <AutoAwesomeIcon sx={{ fontSize: 26, color: '#fff' }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.3px', mb: 0.5 }}>
          Penlytics、本日公開。
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', opacity: 0.95, lineHeight: 1.6 }}>
          AIと一緒に書いて、書いた先でポイントが回ります。
        </Typography>
      </Box>

      {/* 本文 */}
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
        <Bullet><strong>AI機能を使う</strong>とポイントを消費（本文生成・レビュー・分析 など）</Bullet>
        <Bullet>ほかのユーザーに<strong>ポイントをギフトして応援</strong>／逆に<strong>応援を受け取る</strong>こともできる（手数料0%）</Bullet>
        <Bullet>貯まったポイントは <strong>Amazonギフト券に換金</strong>できる（本人確認後）</Bullet>
        <Bullet>あいぺんでの<strong>日々の活動でもポイントが貯まる</strong></Bullet>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
          <Button
            onClick={goGuide}
            fullWidth
            variant="contained"
            sx={{
              py: 1.2, borderRadius: '10px',
              textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
              background: PURPLE_GRAD,
              boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              '&:hover': { background: PURPLE_GRAD, opacity: 0.92 },
            }}
          >
            ガイドを見る
          </Button>
          <Button
            onClick={close}
            sx={{
              py: 1.2, px: 2.5, borderRadius: '10px',
              textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
              color: '#536471', flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            閉じる
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LaunchAnnounceModal;
